import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'

/* ═══════════════════════════════════════════════════════════
   SubscriptionModal — AirLog paywall
   - Mobile: bottom sheet (slides up)
   - Desktop / iPad: centered card (fades + lifts in)
   Pure presentation. The "Start Free Trial" button calls
   onStartTrial() — you wire that to Apple In-App Purchase /
   Google Play Billing later (via Capacitor). This file never
   touches real payment; Apple/Google show their own native
   payment sheet when you trigger the purchase in that handler.
   ═══════════════════════════════════════════════════════════ */

const CONFIG = {
  // ─── Breakpoint ─────────────────────────────────────
  mobileMaxWidth: 768,          // <= this width uses the bottom-sheet layout

  // ─── Backdrop ───────────────────────────────────────
  backdropColor: 'rgba(0, 0, 0, 0.55)',
  backdropBlur: 2,              // px

  // ─── Sheet / card surface ───────────────────────────
  surfaceBg: '#1c1c1e',         // dark sheet background
  sheetRadius: 28,              // px — top corners (mobile) / all corners (desktop)
  cardMaxWidth: 420,            // px — desktop/iPad card width
  cardRadius: 28,               // px

  // ─── Spacing ────────────────────────────────────────
  padX: 28,                     // px horizontal padding
  padTop: 24,                   // px top padding (below handle/close)
  padBottom: 28,                // px bottom padding

  // ─── Drag handle (mobile only) ──────────────────────
  handleW: 38,
  handleH: 5,
  handleColor: 'rgba(255,255,255,0.28)',

  // ─── Close (X) button ───────────────────────────────
  closeSize: 30,                // px diameter
  closeBg: 'rgba(120,120,128,0.32)',
  closeIcon: 'rgba(235,235,245,0.65)',

  // ─── App icon ───────────────────────────────────────
  iconSize: 84,                 // px
  iconRadius: 20,               // px (iOS squircle-ish)
  iconShadow: '0 8px 24px rgba(0,0,0,0.35)',

  // ─── Headline ───────────────────────────────────────
  titleSize: 34,                // px
  titleWeight: 800,
  titleColor: '#ffffff',
  titleLineHeight: 1.08,

  // ─── Description ────────────────────────────────────
  descSize: 17,
  descWeight: 400,
  descColor: 'rgba(235,235,245,0.6)',
  descLineHeight: 1.4,

  // ─── Price line ─────────────────────────────────────
  priceSize: 15,
  priceWeight: 500,
  priceColor: 'rgba(235,235,245,0.85)',

  // ─── CTA button ─────────────────────────────────────
  ctaBg: '#007AFF',             // app blue
  ctaText: '#ffffff',
  ctaSize: 17,
  ctaWeight: 600,
  ctaHeight: 54,
  ctaRadius: 14,

  // ─── Footer links ───────────────────────────────────
  linkColor: '#0A84FF',
  linkSize: 14,

  // ─── Copy (text content) ────────────────────────────
  title: 'Start your free,\none month trial.',
  description: 'A permanent second memory across all your devices. Capture notes, files, photos, links, code, and voice logs in one private space.',
  priceLine: '1 month free, then RM7.00/month.',
  ctaLabel: 'Start Free Trial',
  termsLabel: 'Terms and Condition',
  servicesLabel: 'Services',

  // ─── Motion ─────────────────────────────────────────
  sheetSpring: { type: 'spring', stiffness: 380, damping: 36, mass: 0.9 },
  cardSpring: { type: 'spring', stiffness: 320, damping: 30 },
  backdropFade: 0.25,           // seconds
}

/* Small X icon (SF-style) */
const CloseIcon = ({ size = 12, color }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.19336 8.45508C9.39258 8.6543 9.39258 8.98828 9.19336 9.19336C8.99414 9.39844 8.64844 9.39258 8.44922 9.19336L4.69922 5.44336L0.949219 9.19336C0.75 9.39258 0.410156 9.39258 0.205078 9.19336C0 8.99414 0.00585938 8.6543 0.205078 8.45508L3.95508 4.69922L0.205078 0.949219C0.00585938 0.75 0.00585938 0.416016 0.205078 0.210938C0.404297 0 0.75 0.00585938 0.949219 0.210938L4.69922 3.96094L8.44922 0.210938C8.6543 0 8.98828 0.00585938 9.19336 0.210938C9.39844 0.410156 9.39258 0.75 9.19336 0.949219L5.44336 4.69922L9.19336 8.45508Z" fill={color} />
  </svg>
)

/* Fallback icon if no appIcon image is available.
   By default we pass '/airlog-icon.png' (the same asset Auth.js uses),
   so this gradient placeholder only shows if the image fails to load. */
const FallbackIcon = () => (
  <div style={{
    width: '100%', height: '100%',
    borderRadius: CONFIG.iconRadius,
    background: 'linear-gradient(160deg, #4DA3FF 0%, #007AFF 100%)',
  }} />
)

function SubscriptionModal({
  show,
  onClose,
  onStartTrial,
  onOpenTerms,
  onOpenServices,
  appIcon = '/airlog-icon.png',   // ← real AirLog icon (same path Auth.js uses)
}) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= CONFIG.mobileMaxWidth : true
  )
  const [iconFailed, setIconFailed] = useState(false)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= CONFIG.mobileMaxWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Lock body scroll while open
  useEffect(() => {
    if (show) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [show])

  /* The shared inner content (icon, title, desc, price, CTA, links).
     Same markup for both layouts — only the wrapper differs. */
  const content = (
    <>
      {/* App icon */}
      <div style={{
        width: CONFIG.iconSize,
        height: CONFIG.iconSize,
        borderRadius: CONFIG.iconRadius,
        boxShadow: CONFIG.iconShadow,
        marginBottom: 24,
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {appIcon && !iconFailed
          ? <img
              src={appIcon}
              alt="AirLog"
              onError={() => setIconFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: CONFIG.iconRadius }}
            />
          : <FallbackIcon />}
      </div>

      {/* Headline */}
      <h1 style={{
        fontSize: CONFIG.titleSize,
        fontWeight: CONFIG.titleWeight,
        color: CONFIG.titleColor,
        lineHeight: CONFIG.titleLineHeight,
        margin: 0,
        marginBottom: 16,
        whiteSpace: 'pre-line',
        fontFamily: 'var(--font-system)',
        letterSpacing: '-0.02em',
      }}>
        {CONFIG.title}
      </h1>

      {/* Description */}
      <p style={{
        fontSize: CONFIG.descSize,
        fontWeight: CONFIG.descWeight,
        color: CONFIG.descColor,
        lineHeight: CONFIG.descLineHeight,
        margin: 0,
        fontFamily: 'var(--font-system)',
      }}>
        {CONFIG.description}
      </p>

      {/* Spacer pushes price + button toward the bottom */}
      <div style={{ flex: 1, minHeight: 28 }} />

      {/* Price line */}
      <p style={{
        fontSize: CONFIG.priceSize,
        fontWeight: CONFIG.priceWeight,
        color: CONFIG.priceColor,
        textAlign: 'center',
        margin: 0,
        marginBottom: 14,
        fontFamily: 'var(--font-system)',
      }}>
        {CONFIG.priceLine}
      </p>

      {/* CTA */}
      <motion.button
        onClick={onStartTrial}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        style={{
          width: '100%',
          height: CONFIG.ctaHeight,
          borderRadius: CONFIG.ctaRadius,
          background: CONFIG.ctaBg,
          color: CONFIG.ctaText,
          fontSize: CONFIG.ctaSize,
          fontWeight: CONFIG.ctaWeight,
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-system)',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {CONFIG.ctaLabel}
      </motion.button>

      {/* Footer links */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 18,
      }}>
        <button onClick={onOpenTerms} style={linkStyle}>{CONFIG.termsLabel}</button>
        <span style={{ color: CONFIG.linkColor, fontSize: CONFIG.linkSize, opacity: 0.5 }}>|</span>
        <button onClick={onOpenServices} style={linkStyle}>{CONFIG.servicesLabel}</button>
      </div>
    </>
  )

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: CONFIG.backdropFade }}
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: CONFIG.backdropColor,
            backdropFilter: `blur(${CONFIG.backdropBlur}px)`,
            WebkitBackdropFilter: `blur(${CONFIG.backdropBlur}px)`,
            zIndex: 4000,
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
          }}
        >
          {/* ── MOBILE: bottom sheet ── */}
          {isMobile ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={CONFIG.sheetSpring}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                maxHeight: '92dvh',
                background: CONFIG.surfaceBg,
                borderTopLeftRadius: CONFIG.sheetRadius,
                borderTopRightRadius: CONFIG.sheetRadius,
                paddingLeft: CONFIG.padX,
                paddingRight: CONFIG.padX,
                paddingTop: 12,
                paddingBottom: `calc(${CONFIG.padBottom}px + env(safe-area-inset-bottom, 0px))`,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: '70dvh',
              }}
            >
              {/* drag handle */}
              <div style={{
                width: CONFIG.handleW,
                height: CONFIG.handleH,
                borderRadius: CONFIG.handleH,
                background: CONFIG.handleColor,
                margin: '0 auto 8px',
                flexShrink: 0,
              }} />

              {/* close button */}
              <button onClick={onClose} style={{ ...closeBtnStyle, position: 'absolute', top: 18, right: 18 }}>
                <CloseIcon color={CONFIG.closeIcon} />
              </button>

              <div style={{ paddingTop: CONFIG.padTop, display: 'flex', flexDirection: 'column', flex: 1 }}>
                {content}
              </div>
            </motion.div>
          ) : (
            /* ── DESKTOP / iPad: centered card ── */
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={CONFIG.cardSpring}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: CONFIG.cardMaxWidth,
                background: CONFIG.surfaceBg,
                borderRadius: CONFIG.cardRadius,
                padding: `${CONFIG.padTop + 14}px ${CONFIG.padX}px ${CONFIG.padBottom}px`,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: 560,
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
              }}
            >
              <button onClick={onClose} style={{ ...closeBtnStyle, position: 'absolute', top: 18, right: 18 }}>
                <CloseIcon color={CONFIG.closeIcon} />
              </button>
              {content}
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* shared styles */
const closeBtnStyle = {
  width: CONFIG.closeSize,
  height: CONFIG.closeSize,
  borderRadius: '50%',
  background: CONFIG.closeBg,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  zIndex: 5,
  WebkitTapHighlightColor: 'transparent',
}

const linkStyle = {
  background: 'none',
  border: 'none',
  color: CONFIG.linkColor,
  fontSize: CONFIG.linkSize,
  cursor: 'pointer',
  padding: 0,
  fontFamily: 'var(--font-system)',
}

export default SubscriptionModal